import React, { createContext, useContext, useCallback, useState, useEffect, useRef } from 'react';
import { generateCampaign, fetchFlowModels } from '../services/api';
import { useAppContext } from './AppContext';
import { useToastState, ToastContext } from '../hooks/useToast';
import { useActiveTabState, ActiveTabContext } from '../hooks/useActiveTab';
import type { GenConfig, GeneratedResult, FlowModel } from '../types';
import type { RestoredSession } from '../services/api/generationSession';

/** 從圖片庫「繼續生成」帶進 Result 頁的還原請求 */
export interface GenerationRestoreRequest {
    /** 每次點擊都遞增，讓消費端能辨識這是一次新的還原 */
    token: number;
    session: RestoredSession;
    /** 使用者點的那張圖（還原後預覽中的圖） */
    focusFileId: string;
    /** 該張圖原本的生成時間 */
    originalCreatedAt: string;
}

// ── GenContext 只保留生成相關狀態 ──────────────────────────────────────────────

interface GenContextValue {
    /** @deprecated 請改用 useActiveTab().activeTab */
    activeTab: string;
    /** @deprecated 請改用 useActiveTab().setActiveTab */
    setActiveTab: (tab: string) => void;
    /** @deprecated 請改用 useActiveTab().setNavigationGuard */
    setNavigationGuard: (guard: (() => boolean) | null) => void;
    genStep: number;
    setGenStep: React.Dispatch<React.SetStateAction<number>>;
    genConfig: GenConfig;
    setGenConfig: React.Dispatch<React.SetStateAction<GenConfig>>;
    isGenerating: boolean;
    generatedResult: GeneratedResult | null;
    handleGenerate: () => Promise<void>;
    applyToolkit: (item: FlowModel) => void;
    appliedToolkit: FlowModel | null;
    returnToToolkitList: () => void;
    /** 待套用的還原請求；由 QuickGen 流程消費 */
    restoreRequest: GenerationRestoreRequest | null;
    /** 從圖片庫發起「繼續生成」：切到工具包頁並直接進 Result 頁套用該次流程資料 */
    startGenerationRestore: (session: RestoredSession, focusFileId: string, originalCreatedAt: string) => void;
    clearGenerationRestore: () => void;
    /** @deprecated 請改用 useToast().toast */
    toast: string | null;
    /** @deprecated 請改用 useToast().clearToast */
    clearToast: () => void;
}

const GenContext = createContext<GenContextValue | null>(null);

export const useGenContext = (): GenContextValue => {
    const ctx = useContext(GenContext);
    if (!ctx) throw new Error('useGenContext must be used within GenProvider');
    return ctx;
};

/**
 * 初始生成設定不預先填任何題目答案。
 *
 * 預設值一律由當前工具包的 `questions[].defaultValue` 決定（見 flowDefaults.ts）。
 * 過去這裡硬編了四個內建題目的初始值，但工具包的題目可以完全自訂，
 * 沒被宣告的題目其答案不會進入 prompt，只會變成存進紀錄的死資料。
 */
const DEFAULT_GEN_CONFIG: GenConfig = {
    answers: {},
    uploadedAssets: [],
    uploadedPreviewUrls: [],
};

export const GenProvider: React.FC<{ children: React.ReactNode; }> = ({ children }) => {
    const { organizationName, organizationType, name, title, isAuthenticated } = useAppContext();

    // ── Toast 狀態（抽出為獨立 hook） ─────────────────────────────────────────
    const toastState = useToastState();

    // ── 生成相關狀態 ──────────────────────────────────────────────────────────
    const [genStep, setGenStep] = useState(1);
    const [genConfig, setGenConfig] = useState<GenConfig>(DEFAULT_GEN_CONFIG);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedResult, setGeneratedResult] = useState<GeneratedResult | null>(null);
    const [appliedToolkit, setAppliedToolkit] = useState<FlowModel | null>(null);

    // ── 從圖片庫還原生成流程 ──────────────────────────────────────────────────
    const [restoreRequest, setRestoreRequest] = useState<GenerationRestoreRequest | null>(null);
    const restoreTokenRef = useRef(0);
    // 還原當下會主動切到工具包頁，這一次的 tab 切換不可重置狀態（否則剛套用的資料會被清掉）
    const pendingRestoreRef = useRef(false);

    // ── Tab 切換時重置生成狀態 ────────────────────────────────────────────────
    const handleTabChange = useCallback((_from: string, _to: string) => {
        if (pendingRestoreRef.current) return;
        setGenStep(1);
        setAppliedToolkit(null);
        setGenConfig(prev => ({ ...prev, flowModelId: undefined, answers: {} }));
        setRestoreRequest(null);
    }, []);

    const activeTabState = useActiveTabState('toolkit', handleTabChange);

    // 從工具包套用 → 維持在工具包 tab，直接展開流程
    const applyToolkit = (item: FlowModel) => {
        setGenConfig(prev => ({ ...prev, flowModelId: item.id, answers: {} }));
        setAppliedToolkit(item);
        setGenStep(1);
        toastState.showToast(`已套用《${item.title}》`);
    };

    /**
     * 從圖片庫「繼續生成」。
     * 只是切換畫面並套用該次流程的資料 —— 不會建立任何紀錄，
     * 新紀錄要等使用者真的按下生成才由後端建立（分支）。
     */
    const startGenerationRestore = useCallback(
        (session: RestoredSession, focusFileId: string, originalCreatedAt: string) => {
            restoreTokenRef.current += 1;
            pendingRestoreRef.current = true;
            setRestoreRequest({ token: restoreTokenRef.current, session, focusFileId, originalCreatedAt });
            activeTabState.setActiveTab('toolkit');
        },
        [activeTabState],
    );

    const clearGenerationRestore = useCallback(() => setRestoreRequest(null), []);

    // 套用還原請求：填回生成設定、直接進 Result 頁，不重跑問答步驟
    useEffect(() => {
        if (!restoreRequest) {
            pendingRestoreRef.current = false;
            return;
        }
        const { session } = restoreRequest;
        setGenConfig(prev => ({
            ...prev,
            flowModelId: session.flowModelId,
            answers: session.answers,
            imageAnswers: session.imageAnswers,
            additionalRequirements: session.additionalRequirements,
            uploadedAssets: [],
            uploadedPreviewUrls: [],
            mode: undefined,
            continuationFeedback: undefined,
        }));
        setGenStep(4);

        // QuickGen 只在 appliedToolkit 非 null 時掛載，故必須補上對應的工具包
        const fallback: FlowModel = { id: session.flowModelId, title: '繼續生成', questions: [] };
        fetchFlowModels()
            .then((models: FlowModel[]) => {
                setAppliedToolkit(models.find(m => m.id === session.flowModelId) ?? fallback);
            })
            .catch(() => setAppliedToolkit(fallback))
            .finally(() => { pendingRestoreRef.current = false; });
    }, [restoreRequest]);

    // ── 外部系統 deep link：登入後，依 URL 的 flowModelId 自動套用對應 toolkit ──
    // 沒做這步的話，QuickGen 元件根本不會掛載（App.tsx 只在 appliedToolkit 非 null 時渲染）
    const deeplinkAppliedRef = useRef(false);
    useEffect(() => {
        if (deeplinkAppliedRef.current) return;
        if (!isAuthenticated) return;

        const params = new URLSearchParams(window.location.search);
        const token = params.get('payloadToken');
        const flowModelId = params.get('flowModelId');
        if (!token || !flowModelId) return;

        deeplinkAppliedRef.current = true;
        fetchFlowModels()
            .then((models: FlowModel[]) => {
                const found = models.find(m => m.id === flowModelId);
                if (!found) {
                    console.warn('[deeplink] 找不到 flowModelId：', flowModelId);
                    deeplinkAppliedRef.current = false;
                    return;
                }
                // 直接進入 QuickGen 視圖，後續 useQuickGenFlow 會用 token 完成 redeem 填表
                setAppliedToolkit(found);
                setGenConfig(prev => ({ ...prev, flowModelId: found.id, answers: {} }));
                setGenStep(1);
            })
            .catch((err: Error) => {
                console.error('[deeplink] fetchFlowModels 失敗：', err);
                deeplinkAppliedRef.current = false;
            });
    }, [isAuthenticated]);

    // 從 QuickGen 流程返回工具包列表（防呆邏輯由呼叫端處理）
    const returnToToolkitList = () => {
        setAppliedToolkit(null);
        setGenStep(1);
        setGenConfig(prev => ({ ...prev, flowModelId: undefined, answers: {} }));
        setRestoreRequest(null);
    };

    const handleGenerate = async () => {
        setIsGenerating(true);
        setGeneratedResult(null);
        try {
            const result = await generateCampaign({
                name,
                title,
                organizationName,
                organizationType,
                content: (genConfig.answers['contentInImage'] as string) ?? '',
                tone: (genConfig.answers['textToneInImage'] as string) ?? '',
                style: (genConfig.answers['imageStyle'] as string) ?? '',
                ratio: (genConfig.answers['imageAspectRatio'] as string) ?? '',
                toolkitId: genConfig.flowModelId,
                assets: genConfig.uploadedAssets,
                usePersonalPhoto: (genConfig.answers['usePersonalPhoto'] as boolean) ?? false,
            });
            setGeneratedResult({
                text: result.text,
                background: result.background,
                aiLayoutResult: result.aiLayoutResult,
            });
            setGenStep(3);
        } catch (error) {
            console.error('Generation error:', error);
            alert('生成失敗，請稍後再試或檢查後端連線。');
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <ActiveTabContext.Provider value={activeTabState}>
            <ToastContext.Provider value={toastState}>
                <GenContext.Provider value={{
                    activeTab: activeTabState.activeTab,
                    setActiveTab: activeTabState.setActiveTab,
                    setNavigationGuard: activeTabState.setNavigationGuard,
                    genStep, setGenStep,
                    genConfig, setGenConfig,
                    isGenerating, generatedResult,
                    handleGenerate, applyToolkit,
                    appliedToolkit, returnToToolkitList,
                    restoreRequest, startGenerationRestore, clearGenerationRestore,
                    toast: toastState.toast,
                    clearToast: toastState.clearToast,
                }}>
                    {children}
                </GenContext.Provider>
            </ToastContext.Provider>
        </ActiveTabContext.Provider>
    );
};
